import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

// Types for article metadata
interface Article {
  id: string;
  title: string;
  slug: string;
  publishedAt: string;
  readTime: string;
  featured: boolean;
  category: string;
  tags: string[];
  content?: string;
}

interface MetaData {
  lastUpdated: string;
  summary: string;
  categories: {
    id: string;
    name: string;
    description: string;
    icon: string;
    color: string;
    articleIds: string[];
  }[];
}

const GuidesHub = () => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [parsedMeta, setParsedMeta] = useState<MetaData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const parseFrontMatter = (text: string) => {
    const lines = text.split('\n');
    let inFrontMatter = false;
    const data: any = {};
    let contentStartIndex = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      if (line.trim() === '---') {
        if (!inFrontMatter) {
          inFrontMatter = true;
        } else {
          contentStartIndex = i + 1;
          break;
        }
      } else if (inFrontMatter) {
        const colonIndex = line.indexOf(':');
        if (colonIndex !== -1) {
          const key = line.slice(0, colonIndex).trim();
          let value = line.slice(colonIndex + 1).trim();

          if (value.startsWith('[') && value.endsWith(']')) {
            data[key] = value
              .slice(1, -1)
              .split(',')
              .map((item: string) => item.trim().replace(/^"|"$/g, ''));
          } else {
            value = value.replace(/^"|"$/g, '');
            if (value === 'true') {
              data[key] = true;
            } else if (value === 'false') {
              data[key] = false;
            } else {
              data[key] = value;
            }
          }
        }
      }
    }

    const content = lines.slice(contentStartIndex).join('\n').trim();

    return { data, content };
  };

  useEffect(() => {
    const loadArticles = async () => {
      try {
        const metaResponse = await fetch('/data/guides/articles/meta.json');
        if (!metaResponse.ok) {
          throw new Error(`Failed to load meta: ${metaResponse.status}`);
        }
        const metaText = await metaResponse.text();
        const meta = JSON.parse(metaText) as MetaData;
        console.log('Meta loaded:', meta);
        setParsedMeta(meta);

        const allArticles: Article[] = [];
        const articlePromises: Promise<void>[] = [];

        for (const category of meta.categories) {
          for (const articleId of category.articleIds) {
            const promise = (async () => {
              try {
                const articleResponse = await fetch(`/data/guides/articles/${articleId}.md`);
                if (!articleResponse.ok) {
                  console.error(`Failed to load article ${articleId}:`, articleResponse.status);
                  return;
                }

                const text = await articleResponse.text();
                const { data, content } = parseFrontMatter(text);

                allArticles.push({
                  id: articleId,
                  title: data.title,
                  slug: data.slug,
                  publishedAt: data.publishedAt,
                  readTime: data.readTime,
                  featured: data.featured || false,
                  category: data.category,
                  tags: data.tags || [],
                  content
                });
              } catch (err) {
                console.error(`Error parsing article ${articleId}:`, err);
              }
            })();

            articlePromises.push(promise);
          }
        }

        await Promise.all(articlePromises);
        console.log('All articles loaded:', allArticles.length);
        setArticles(allArticles);
      } catch (error) {
        console.error('Error loading articles:', error);
        setLoadError(error instanceof Error ? error.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    loadArticles();
  }, []);

  const filteredArticles = selectedCategory
    ? articles.filter(article => article.category === selectedCategory)
    : articles;

  return (
    <div className="container-custom">
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold mb-4">
          Healthcare Guides & Resources
        </h1>
        <p className="text-lg text-gray-600 mb-6">
          Complete guides from visa application to post-discharge
        </p>
        {parsedMeta && (
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-primary-800">
              <strong>Last Updated:</strong> {parsedMeta.lastUpdated} • <strong>Total Articles:</strong> {articles.length}
            </p>
          </div>
        )}
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        <aside className="lg:w-72 flex-shrink-0">
          <div className="sticky top-24 bg-white border border-gray-200 rounded-lg p-4">
            <h2 className="text-lg font-bold mb-4">Categories</h2>
            <nav className="space-y-2">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                  selectedCategory === null
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                All Guides ({articles.length})
              </button>
              {articles.length > 0 && parsedMeta && (
                parsedMeta.categories.map(category => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      selectedCategory === category.id
                        ? 'bg-primary-600 text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span>{category.icon}</span>
                      <span className="flex-1">{category.name}</span>
                      <span className="text-sm text-gray-400">
                        {category.articleIds.length}
                      </span>
                    </div>
                  </button>
                ))
              )}
            </nav>
          </div>
        </aside>

        <main className="flex-1">
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6">
              {selectedCategory
                ? parsedMeta?.categories.find(c => c.id === selectedCategory)?.name
                : 'All Articles'}
            </h2>
            {loading ? (
              <div className="text-center py-12 text-gray-500">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              </div>
            ) : loadError ? (
              <div className="text-center py-12 text-red-500">
                <p>Error loading articles: {loadError}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-4 text-primary-600 hover:underline"
                >
                  Retry
                </button>
              </div>
            ) : filteredArticles.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p>No articles found in this category.</p>
                <button
                  onClick={() => setSelectedCategory(null)}
                  className="mt-4 text-primary-600 hover:underline"
                >
                  View All Guides
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredArticles.map(article => (
                  <article
                    key={article.id}
                    className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                  >
                    {article.featured && (
                      <div className="bg-primary-600 text-white text-xs font-semibold px-3 py-1">
                        Featured
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xs text-gray-500">
                          {article.publishedAt}
                        </span>
                        <span className="text-xs text-gray-400">•</span>
                        <span className="text-xs text-gray-500">
                          {article.readTime}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold mb-2 hover:text-primary-600">
                        {article.title}
                      </h3>
                      <p className="text-gray-600 mb-4 line-clamp-2">
                        {article.content?.substring(0, 150)}...
                      </p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {article.tags.map((tag, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      <Link
                        to={`/guides/${article.slug}`}
                        className="inline-flex items-center text-primary-600 font-medium hover:text-primary-700"
                      >
                        Read Article
                        <svg
                          className="w-4 h-4 ml-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        </main>
      </div>

      <section className="mb-12">
        <h2 className="text-2xl font-bold mb-6">Popular Categories</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {articles.length > 0 && parsedMeta && (
            parsedMeta.categories.map(category => (
              <div
                key={category.id}
                className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setSelectedCategory(category.id)}
              >
                <div className="text-4xl mb-3">{category.icon}</div>
                <h3 className="text-lg font-bold mb-2">{category.name}</h3>
                <p className="text-sm text-gray-600 mb-4">
                  {category.description}
                </p>
                <div className="text-sm text-primary-600 font-medium">
                  {category.articleIds.length} Articles →
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
};

export default GuidesHub;
